import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import "./UserDetails.css";
import "react-vis/dist/style.css";
import {
  Breadcrumb,
  Row,
  Col,
  Collapse,
  Tabs,
  Divider,
  Button,
  Input,
  InputNumber,
  Card,
  Modal,
  Form,
  Pagination,
  message,
  Space,
  Select,
  Typography,
  Tooltip,
  Descriptions,
  Tag,
} from "antd";
import {
  UserOutlined,
  PhoneFilled,
  MailFilled,
  IdcardFilled,
  CreditCardFilled,
  PayCircleOutlined,
  BankFilled,
  LoadingOutlined,
  CaretRightOutlined,
  FileTextOutlined,
  NumberOutlined,
  IdcardOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  GlobalOutlined
} from "@ant-design/icons";
import axios from "axios";
import AuditLogList from "./AuditLogList";
import UserCodOrder from "./UserCodOrder";
import UserReturnedOrder from "./UserReturnedOrder";
import UserAnalytic from "./UserAnalytic";
import UserOrder from "./UserOrder";
import { formatDateTime, getRelativeTimeFromNow } from "../../helpers/date";
import UserTransaction from "./UserTransaction";
import UserAdminAdjustment from "./UserAdminAdjustment";
import UserOnlinePayment from "./UserOnlinePayment";
import { startCase } from "lodash";
import UserBetaFeatures from "./UserBetaFeatures";
import UserPreferences from "./UserPreferences";
import UserBillingSettings from "./UserBillingSettings";
import UserBilling from "./UserBilling";
import { BILLING_TYPE, DEFAULT_CREDIT_TERM } from "../../constants/billing";
import TopupPackageTag from "../shared/TopupPackageTag";
import { ADMIN_ADJUSTMENT_REMARKS } from "../../constants/adminAdjustmentRemarks";
import { usePermissions } from "../App/PermissionContext";
import { PERMISSIONS } from "../../constants/roles";
import moment from "moment";
import SupportTicketList from "../SupportTicket/SupportTicketList";
import SupportTicketFilters from "../SupportTicket/SupportTicketFilters";
import UserPointHistory from "./UserPointHistory";
import SubAccountDetails from "../SubAccount/SubAccountDetails";
import UserSubAccountActions from "./UserSubAccountActions";
import { getFeatureFlag } from "../../config/featureFlag";
import UserCreditCard from "./UserCreditCard";
import { formatCardProvider, formatCardSummary } from "../../helpers/cardBinding";

const { TabPane } = Tabs;
const { Text } = Typography;

const UserDetails = ({ token }) => {
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const [user, setUser] = useState([]);
  const [merchantId, setMerchantId] = useState("");
  const [topupData, setTopupData] = useState([]);
  const [credit, setCredit] = useState(false);
  const [editDetails, setEditDetails] = useState(false);
  const [addNotes, setAddNotes] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [availableFeePackages, setAvailableFeePackages] = useState([]);
  const [availableCourierPackages, setAvailableCourierPackages] = useState([]);
  const [availableInternationalPackages, setAvailableInternationalPackages] = useState([]);
  const [availableInternationalCourierPackages, setAvailableInternationalCourierPackages] = useState([]);
  const [defaultPackageKeys, setDefaultPackageKeys] = useState({});
  const [auditLogParams, setAuditLogParams] = useState({
    currentPage: 1,
    limit: 10,
  });
  const [auditLogData, setAuditLogData] = useState({
    data: [],
    totalSize: 0,
  });
  const [userNotes, setUserNotes] = useState([]);
  const [userNotesPagination, setUserNotesPagination] = useState({
    currentPage: 1,
    limit: 10,
  });
  const [filters, setFilters] = useState({});
  const [assignConfig, setAssignConfig] = useState(false);

  const [merchant, setMerchant] = useState({});
  const [credits, setCredits] = useState(false);

  const [betaFeatures, setBetaFeatures] = useState([]);
  const [dhlCodLimit, setDhlCodLimit] = useState(0);
  const [preferences, setPreferences] = useState({});
  const [billingType, setBillingType] = useState(undefined);
  const [creditTerm, setCreditTerm] = useState(undefined);
  const { permissions, isAdminTester, billingGateEnabled } = usePermissions();
  const billingAllowed = !billingGateEnabled || isAdminTester;

  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketFilters, setTicketFilters] = useState({
    sort: "updatedAt",
    order: "descending",
  });
  const [assignees, setAssignees] = useState([]);
  const [submitters, setSubmitters] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 50 });

  const routes = [
    {
      path: "",
      breadcrumbName: "App",
    },
    {
      path: "/user",
      breadcrumbName: "User",
    },
    {
      path: `${userId}`,
      breadcrumbName: `${userId}`,
    },
  ];

  const diffInDays = merchant?.packageBoughtAt
    ? (merchant?.packageValidDays || 180) -
    moment().diff(merchant.packageBoughtAt, "days")
    : null;
  const diffLockedInDays = merchant?.packageLockedAt
    ? (merchant?.packageLockedDays || 0) -
    moment().diff(merchant.packageLockedAt, "days")
    : null;

  const loadAuditLog = useCallback(async () => {
    try {
      const res = await axios.get(`/api/user/${id}/audit-logs`, {
        params: {
          page: auditLogParams.currentPage,
          limit: auditLogParams.limit,
        },
      });
      setAuditLogData({
        data: res.data.data,
        totalSize: res.data.pagination.totalSize,
      });
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load audit logs");
    }
  }, [id, auditLogParams.currentPage, auditLogParams.limit]);

  useEffect(() => {
    loadUserDetails();
  }, [token]);

  useEffect(() => {
    if (id) loadAuditLog();
  }, [loadAuditLog]);

  useEffect(() => {
    if (merchantId) {
      console.log(merchant);
      loadTransactionsData();
    }

    if (user.userId && permissions && permissions.includes(PERMISSIONS.VIEW_USER_NOTES)) {
      loadUserNotes();
    }
  }, [merchant, userNotesPagination.currentPage, userNotesPagination.limit]);

  const loadUserNotes = useCallback(async () => {
    const params = new URLSearchParams([
      ["limit", 10],
      ["page", userNotesPagination.currentPage],
    ]);

    const res = await axios.get(`/api/user/${user.userId}/notes`, {
      params,
    });
    setUserNotes(res.data.data);
    setUserNotesPagination(res.data.pagination);
  }, [userNotesPagination.currentPage, userNotesPagination.limit]);

  const loadUserDetails = async (userId) => {
    try {
      const response = await axios.get(`/api/users/all-user?userId=${id}`);
      const user = response.data.data[0];
      setUser(user);
      setMerchant(user.merchant);
      setDefaultPackageKeys(response.data.meta?.defaultPackages || {});
      setCredits(user.merchant.credits);
      setBetaFeatures(user.betaFeatures ? user.betaFeatures : []);
      setDhlCodLimit(user.merchant?.preferences?.dhlCodLimit ?? null);
      setPreferences(user.merchant.preferences ? user.merchant.preferences : {});
      // Billing type/credit term default to Type 1 (Pay Now) / 14 days for merchants that
      // predate this feature and have no preference saved yet — mirrors getMerchantBillingType
      // / getMerchantCreditTerm in the API, so the dropdown shows the true effective value.
      setBillingType(user.merchant?.preferences?.billingType || BILLING_TYPE.PAY_NOW);
      setCreditTerm(user.merchant?.preferences?.creditTerm || DEFAULT_CREDIT_TERM);
      setUserId(user.userId);
      setMerchantId(user.merchant.merchantId);
      // Set other necessary state fields here...
    } catch (err) {
      console.log(err);
    }
  };

  const loadTransactionsData = async () => {
    // const response = await axios.get("/api/transactions");
    const response = await axios.get(`api/transactions`, {
      params: {
        merchantId: merchantId,
        limit: 1000,
        ...(filters.startDate !== null && {
          startDate: filters.startDate?.startOf("day")?.toISOString(),
        }),
        ...(filters.endDate !== null && {
          endDate: filters.endDate?.endOf("day")?.toISOString(),
        }),
        // ...(filters.search && {payment_data
        //   searchField: filters.searchField,
        //   search: filters.search,
        // }),
      },
    });
    let tableData = response.data.data.sort((a, b) =>
      a.createdAt > b.createdAt ? -1 : 1
    );

    // Filter for Top Up transactions
    let topupTransaction = tableData.filter(
      (transaction) => transaction.method === "Top Up"
    );

    setTopupData(topupTransaction);

    // Optionally, log or return the topup transactions
    console.log(topupTransaction);
    // Map transaction data and set state for relevant data...
  };

  const loadPackagesData = async () => {
    try {
      const res1 = await axios.get(`/api/topup-packages`);
      setAvailablePackages(res1.data.meta?.packages || []);

      const res2 = await axios.get(`/api/fee-packages`);
      setAvailableFeePackages(res2.data.meta?.packages || []);

      const res3 = await axios.get(`/api/courier-packages`);
      setAvailableCourierPackages(res3.data.meta?.packages || []);

      const res4 = await axios.get(`/api/international-packages`);
      setAvailableInternationalPackages(res4.data.meta?.packages || []);

      const res5 = await axios.get(`/api/international-courier-packages`);
      setAvailableInternationalCourierPackages(res5.data.meta?.packages || []);
    } catch (err) {
      console.log(err);
    }
  };

  // No package assigned to a merchant for a category falls back to whichever PricingPackageLabel
  // is marked isDefault=true for that category (see PricingPackageLabelRepository.getDefaultKeyByCategory
  // in the API repo) -- not just literally whatever's named "normal". If no label has been marked
  // default yet, there's nothing to fall back to, so just show "-" rather than implying one.
  const packageDisplayValue = (value, defaultKey) => {
    if (value) return startCase(value);
    if (defaultKey) return `${startCase(defaultKey)} (default)`;
    return "-";
  };

  const packagePlaceholder = (defaultKey) =>
    defaultKey ? `${startCase(defaultKey)} (default)` : "Choose Package";

  const handleAssignConfig = async () => {
    try {
      await axios.patch(
        `/api/merchant/${merchantId}/service-provider-configs`,
        {
          dhl: {
            api: "https://api.dhlecommerce.dhl.com",
            clientID: "LTY0NzI2OTAwOA==",
            password: "customerpassword@23113120194",
            soldto: "5298362955",
            pickup: "5298362955",
            prefix: "MYJZS",
          },
        }
      );
      message.info("This user has been assigned to the DHL custom configs.");
    } catch (err) {
      message.error("You are not authorized to perform this operation.");
    }
    setAssignConfig(false);
  };

  const handleResetPackage = async () => {
    try {
      await axios.patch(`/api/merchants/${merchantId}/reset-package`);
      message.info(
        "This user Topup Package has been reset. Refresh to verify the change."
      );
    } catch (err) {
      message.error(err.response?.data?.message || "Something went wrong.");
    }
  };

  const fetchSupportTickets = useCallback(async () => {
    setTicketsLoading((prevLoading) => ({ ...prevLoading, index: true }));

    try {
      const params = {
        limit: pagination.limit,
        page: pagination.currentPage,
        searchField: "userEmail",
        search: user.email,
        startDate: ticketFilters.startDate?.startOf("day")?.toISOString(),
        endDate: ticketFilters.endDate?.endOf("day")?.toISOString(),
        updatedStartDate: ticketFilters.updatedStartDate
          ?.startOf("day")
          ?.toISOString(),
        updatedEndDate: ticketFilters.updatedEndDate
          ?.endOf("day")
          ?.toISOString(),
        ...(ticketFilters.status && { status: ticketFilters.status }),
        ...(ticketFilters.category && { category: ticketFilters.category }),
        ...(ticketFilters.assigneeId && {
          assigneeId: ticketFilters.assigneeId,
        }),
        ...(ticketFilters.submittedBy?.length && {
          submittedBy: ticketFilters.submittedBy,
        }),
        sort: ticketFilters.sort,
        order: ticketFilters.order,
      };
      const res = await axios.get("/api/issues", { params });

      setSupportTickets(res.data.data || []);
      setPagination(res.data.pagination);
    } catch (error) {
      message.error(error.response?.data.message || error.message);
    }

    setTicketsLoading((prevLoading) => ({ ...prevLoading, index: false }));
  }, [user, pagination.currentPage, pagination.limit, ticketFilters]);

  const fetchAssignees = useCallback(async () => {
    try {
      const res = await axios.get("/api/issues/assignees");
      setAssignees(res.data.data);
    } catch (error) {
      console.error("Error fetching assignees:", error);
    }
  }, []);

  const fetchSubmitters = useCallback(async () => {
    try {
      const res = await axios.get("/api/issues/submitters");
      setSubmitters(res.data.data);
    } catch (error) {
      console.error("Error fetching submitters:", error);
    }
  }, []);

  useEffect(() => {
    if (!user.email) return;
    fetchSupportTickets();
  }, [user, pagination.currentPage, pagination.limit, fetchSupportTickets]);

  useEffect(() => {
    fetchAssignees();
    fetchSubmitters();
  }, [fetchAssignees, fetchSubmitters]);

  return (
    <div>
      <div className="header">
        <Row>
          <Col span={12}>
            <h2 className="detail-header-text">User Details</h2>
          </Col>
          <Col span={12}>
            <Breadcrumb
              style={{ margin: "10px 0", textAlign: "right" }}
              routes={routes}
            />
          </Col>
        </Row>
      </div>

      <Row>
        <Col span={12}></Col>
        <Col span={12} style={{ textAlign: "right" }}>
          {permissions?.includes(PERMISSIONS.ASSIGN_DHL_CUSTOM_CONFIG) && (
            <Button
              type="primary"
              size="middle"
              style={{ margin: 10 }}
              onClick={() => {
                setAssignConfig(true);
              }}
            >
              DHL Custom Config
            </Button>
          )}
          <Modal
            visible={assignConfig}
            title="Dhl Custom Config"
            onOk={async (val) => {
              const credit = parseFloat(val.credit);
              await axios
                .patch(`/api/merchant/${merchantId}/service-provider-configs`, {
                  dhl: {
                    api: "https://api.dhlecommerce.dhl.com",
                    clientID: "LTY0NzI2OTAwOA==",
                    password: "customerpassword@23113120194",
                    soldto: "5298362955",
                    pickup: "5298362955",
                    prefix: "MYJZS",
                  },
                })
                .then(function (response) {
                  message.info(
                    `This user have been assign to the DHL custom configs.`
                  );
                })
                .catch((err) => {
                  message.error(
                    "You are not authorized to perform this operation."
                  );
                });
              setAssignConfig(false);
            }}
            onCancel={() => {
              setAssignConfig(false);
            }}
          >
            <div>Assign this user to the DHL custom configs ?</div>
          </Modal>
          {(permissions.includes(PERMISSIONS.ASSIGN_PACKAGE) || permissions.includes(PERMISSIONS.VIEW_PACKAGE)) &&
            <Button
              type="primary"
              size="middle"
              style={{ margin: 10 }}
              onClick={() => {
                loadPackagesData();
                setShowPackageModal(true);
              }}
            >
              {permissions.includes(PERMISSIONS.ASSIGN_PACKAGE) ? "Manage Package" : "Package Info"}
            </Button>
          }
          <Modal
            title="Manage Package"
            visible={showPackageModal}
            footer={null}
            width={640}
            onCancel={() => {
              setShowPackageModal(false);
            }}
          >
            <Descriptions
              title="Current Packages"
              className="package-descriptions"
              bordered
              size="small"
              column={2}
              style={{ marginBottom: 16 }}
              labelStyle={{ fontWeight: 600, background: "#e8e8e8", width: 160 }}
              contentStyle={{ background: "#fff" }}
            >
              <Descriptions.Item label="Domestic Postage">
                {packageDisplayValue(merchant?.topupPackage, defaultPackageKeys.domestic)}
              </Descriptions.Item>
              <Descriptions.Item label="Domestic Courier">
                {packageDisplayValue(merchant?.courierPackage, defaultPackageKeys.courier)}
              </Descriptions.Item>
              <Descriptions.Item label="International Postage">
                {packageDisplayValue(merchant?.internationalPackage, defaultPackageKeys.international)}
              </Descriptions.Item>
              <Descriptions.Item label="International Courier">
                {packageDisplayValue(merchant?.internationalCourierPackage, defaultPackageKeys.internationalCourier)}
              </Descriptions.Item>
              <Descriptions.Item label="Fee" span={2}>
                {packageDisplayValue(merchant?.feePackage, defaultPackageKeys.fee)}
              </Descriptions.Item>
            </Descriptions>

            <Typography.Text strong>Validity & Lock Status</Typography.Text>
            <Row gutter={16} style={{ marginTop: 8, marginBottom: 16 }}>
              <Col span={12}>
                <Descriptions
                  className="package-descriptions"
                  bordered
                  size="small"
                  column={1}
                  labelStyle={{ fontWeight: 600, background: "#e8e8e8", width: 100 }}
                  contentStyle={{ background: "#fff" }}
                >
                  <Descriptions.Item label="Bought At">
                    {merchant?.packageBoughtAt
                      ? formatDateTime(merchant.packageBoughtAt) +
                      " (" +
                      getRelativeTimeFromNow(merchant.packageBoughtAt) +
                      ")"
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Valid For">
                    {merchant?.packageValidDays || 180} days
                  </Descriptions.Item>
                  <Descriptions.Item label="Expires In">
                    {diffInDays > 0 && diffInDays + " days"}
                    {diffInDays < 0 && (
                      <Typography.Text type="danger">
                        Expired ({Math.abs(diffInDays)} days ago)
                      </Typography.Text>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={12}>
                <Descriptions
                  className="package-descriptions"
                  bordered
                  size="small"
                  column={1}
                  labelStyle={{ fontWeight: 600, background: "#e8e8e8", width: 100 }}
                  contentStyle={{ background: "#fff" }}
                >
                  <Descriptions.Item label="Locked At">
                    {merchant?.packageLockedAt
                      ? formatDateTime(merchant.packageLockedAt) +
                      " (" +
                      getRelativeTimeFromNow(merchant.packageLockedAt) +
                      ")"
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Locked For">
                    {merchant?.packageLockedDays || 0} days
                  </Descriptions.Item>
                  <Descriptions.Item label="Unlocked In">
                    {diffLockedInDays > 0 && diffLockedInDays + " days"}
                    {diffLockedInDays < 0 && (
                      <Typography.Text type="danger">
                        Unlocked ({Math.abs(diffLockedInDays)} days ago)
                      </Typography.Text>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            {permissions.includes(PERMISSIONS.ASSIGN_PACKAGE) && (
              <>
                <Divider>Edit</Divider>
                <Form
                  name="changePackageForm"
                  labelCol={{
                    span: 24,
                  }}
                  wrapperCol={{
                    span: 24,
                  }}
                  initialValues={{
                    remember: true,
                    isPackageLocked: merchant?.isPackageLocked || false,
                    packageLockedDays: merchant?.packageLockedDays || 0,
                    package: merchant?.topupPackage,
                    feePackage: merchant?.feePackage,
                    courierPackage: merchant?.courierPackage,
                    internationalPackage: merchant?.internationalPackage,
                    internationalCourierPackage: merchant?.internationalCourierPackage,
                    packageValidDays: merchant?.packageValidDays || 180,
                  }}
                  onFinish={async (val) => {
                    await axios
                      .put(`api/merchants/${merchantId}/package`, {
                        package: val.package,
                        feePackage: val.feePackage,
                        courierPackage: val.courierPackage,
                        internationalPackage: val.internationalPackage,
                        internationalCourierPackage: val.internationalCourierPackage,
                        packageLockedDays: val.packageLockedDays,
                        packageValidDays: val.packageValidDays,
                      })
                      .then(function (response) {
                        message.info(
                          `The Package type has been updated. Refresh to verify the change.`
                        );
                      })
                      .catch((err) => {
                        message.error(
                          err.response?.data?.message || "Something went wrong."
                        );
                      });
                  }}
                  onFinishFailed={() => { }}
                >
                  <Divider orientation="left" plain>Domestic</Divider>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Postage" name="package">
                        <Select placeholder={packagePlaceholder(defaultPackageKeys.domestic)}>
                          {availablePackages.map((pkg) => (
                            <Select.Option value={pkg}>{startCase(pkg)}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Courier" name="courierPackage">
                        <Select placeholder={packagePlaceholder(defaultPackageKeys.courier)}>
                          {availableCourierPackages.map((pkg) => (
                            <Select.Option value={pkg}>{startCase(pkg)}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider orientation="left" plain>International</Divider>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Postage" name="internationalPackage">
                        <Select placeholder={packagePlaceholder(defaultPackageKeys.international)}>
                          {availableInternationalPackages.map((pkg) => (
                            <Select.Option value={pkg}>{startCase(pkg)}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Courier" name="internationalCourierPackage">
                        <Select placeholder={packagePlaceholder(defaultPackageKeys.internationalCourier)}>
                          {availableInternationalCourierPackages.map((pkg) => (
                            <Select.Option value={pkg}>{startCase(pkg)}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider orientation="left" plain>Other</Divider>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Fee" name="feePackage">
                        <Select placeholder={packagePlaceholder(defaultPackageKeys.fee)}>
                          {availableFeePackages.map((pkg) => (
                            <Select.Option value={pkg}>{startCase(pkg)}</Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Valid For" name="packageValidDays" extra="days">
                        <InputNumber min={1} step={1} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Locked For"
                        name="packageLockedDays"
                        extra="days"
                        tooltip={
                          <p>
                            - To remove lock, set to 0. <br /> - To adjust Lock
                            duration, calculate based on "Locked For" date.
                          </p>
                        }
                      >
                        <InputNumber min={0} step={1} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "6px",
                      height: "100%",
                    }}
                  >
                    <Form.Item
                      wrapperCol={{
                        offset: 1,
                        span: 16,
                      }}
                    >
                      <Button type="primary" htmlType="submit">
                        Update
                      </Button>
                    </Form.Item>

                    <Form.Item
                      wrapperCol={{
                        offset: 1,
                        span: 16,
                      }}
                    >
                      <Button
                        type="primary"
                        onClick={() => {
                          handleResetPackage();
                        }}
                      >
                        Reset Package Locked Date
                      </Button>
                    </Form.Item>
                  </div>
                </Form>
              </>
            )}
          </Modal>

          {permissions?.includes(PERMISSIONS.EDIT_PHONE_EMAIL) && (
            <Button
              type="primary"
              size="middle"
              style={{ margin: 10 }}
              onClick={() => {
                setEditDetails(true);
              }}
            >
              Edit Details
            </Button>
          )}
          <Modal
            title="Edit Details"
            visible={editDetails}
            footer={null}
            onCancel={() => {
              setEditDetails(false);
            }}
            onOk={() => {
              setEditDetails(false);
            }}
          >
            <p>
              <strong>Phone Number</strong> : {user.countryCode + user.phone}
            </p>
            <p>
              <strong>Email</strong> : {user.email}
            </p>
            <Divider>Edit</Divider>
            <Form
              name="changePackageForm"
              labelCol={{
                span: 6,
              }}
              wrapperCol={{
                span: 20,
              }}
              initialValues={{
                email: user.email,
                phone: user.phone,
                countryCode: user.countryCode,
              }}
              onFinish={async (val) => {
                setEditDetails(false);
                await axios
                  .put(`/api/user/update`, {
                    userId: userId,
                    userData: {
                      email: val.email.toLowerCase(),
                      phone: val.phone,
                      countryCode: val.countryCode,
                    },
                  })
                  .then(function (response) {
                    message.info(
                      `The user details has been updated. Refresh to verify the change.`
                    );
                  })
                  .catch((err) => {
                    message.error(
                      err.response?.data?.message || "Something went wrong."
                    );
                  });
              }}
              onFinishFailed={() => { }}
            >
              <Form.Item label="Phone" required>
                <Input.Group compact>
                  <Form.Item
                    name="countryCode"
                    noStyle
                    rules={[{ required: true, message: "Country code is required" }]}
                  >
                    <Select style={{ width: "30%" }}>
                      <Select.Option value="+60">+60</Select.Option>
                      <Select.Option value="+65">+65</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    noStyle
                    dependencies={["countryCode"]}
                    rules={[
                      { required: true, message: "Phone number is required" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const countryCode = getFieldValue("countryCode");

                          if (!value) {
                            return Promise.resolve();
                          }

                          if (!/^\d+$/.test(value)) {
                            return Promise.reject("Phone number must be numeric only");
                          }

                          if (countryCode === "+60" && value.length < 9) {
                            return Promise.reject(
                              "Malaysian numbers must be at least 9 digits"
                            );
                          }

                          if (countryCode === "+65" && value.length < 8) {
                            return Promise.reject(
                              "Singaporean numbers must be at least 8 digits"
                            );
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input style={{ width: "70%" }} />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Email is required" },
                  {
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                wrapperCol={{
                  offset: 1,
                  span: 16,
                }}
              >
                <Button type="primary" htmlType="submit">
                  Update
                </Button>
              </Form.Item>
            </Form>
          </Modal>

          {permissions.includes(PERMISSIONS.ADMIN_MANUAL_TRANSACTION) && (
            <Button
              type="primary"
              size="middle"
              Transaction
              onClick={() => {
                setCredit(true);
              }}
            >
              Manage Credit
            </Button>
          )}
          <Modal
            title="Manage Credit"
            visible={credit}
            footer={null}
            onCancel={() => {
              setCredit(false);
            }}
          >
            <Form
              name="creditform"
              labelCol={{
                span: 4,
              }}
              wrapperCol={{
                span: 20,
              }}
              initialValues={{
                remember: true,
              }}
              onFinish={async (val) => {
                const credit = parseFloat(val.credit);
                await axios
                  .post("/api/user/credit", {
                    credit: credit,
                    user_id: userId,
                    remarks: val.remarks,
                    reference: val.reference,
                  })
                  .then(function (response) {
                    message.info(
                      `The credit has been adjusted. Please refresh to verify the change.`
                    );
                  })
                  .catch((err) => {
                    message.error(
                      "You are not authorized to perform this operation."
                    );
                  });
                setCredit(false);
              }}
              onFinishFailed={() => { }}
            >
              <Form.Item
                label="Credit"
                name="credit"
                rules={[
                  {
                    required: true,
                    message: "Please input your credit!",
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Remarks"
                name="remarks"
                rules={[
                  {
                    required: true,
                    message: "Please select your remarks!",
                  },
                ]}
              >
                <Select>
                  {ADMIN_ADJUSTMENT_REMARKS.map((remark) => {
                    return <Select.Option key={remark}>{remark}</Select.Option>;
                  })}
                </Select>
              </Form.Item>

              <Form.Item label="Reference" name="reference">
                <Input />
              </Form.Item>

              <Form.Item wrapperCol={{ offset: 4, span: 20 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{ marginRight: 8 }}
                >
                  Submit
                </Button>
                <Button
                  onClick={() => {
                    setCredit(false);
                  }}
                >
                  Cancel
                </Button>
              </Form.Item>
            </Form>
          </Modal>
          {permissions?.includes(PERMISSIONS.ADD_USER_NOTES) && (
            <Button
              type="primary"
              size="middle"
              style={{ margin: 10 }}
              onClick={() => {
                setAddNotes(true);
              }}
            >
              Add Notes
            </Button>
          )}
          <Modal
            title="Add Note"
            visible={addNotes}
            footer={null}
            onCancel={() => {
              setAddNotes(false);
            }}
            onOk={() => {
              setAddNotes(false);
            }}
          >
            <Form
              name="addNoteForm"
              labelCol={{
                span: 6,
              }}
              wrapperCol={{
                span: 20,
              }}
              initialValues={{
                note: "",
              }}
              onFinish={async (val) => {
                setAddNotes(false);
                await axios
                  .post(`/api/user/${userId}/notes`, {
                    note: val.note,
                  })
                  .then(function (response) {
                    message.info(
                      `The note has been added. Refresh to verify the change.`
                    );
                    window.location.reload();
                  })
                  .catch((err) => {
                    message.error(
                      err.response?.data?.message || "Something went wrong."
                    );
                  });
              }}
              onFinishFailed={() => { }}
            >
              <Form.Item
                label="Note"
                name="note"
                rules={[{ required: true, message: "Note is required" }]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item
                wrapperCol={{
                  offset: 1,
                  span: 16,
                }}
              >
                <Button type="primary" htmlType="submit">
                  Add
                </Button>
              </Form.Item>
            </Form>
          </Modal>

          <Button
            size="middle"
            className="back-btn"
            onClick={() => (window.location.href = "/user")}
          >
            Back
          </Button>
        </Col>
      </Row>
      <div className="table">
        <Tabs defaultActiveKey="1" animated={true}>
          <TabPane tab="USER DETAILS" key="1">
            <Row className="mb-2 cod-stats" gutter={16}>
              <Col span={6}>
                <Card style={{ width: 300 }}>
                  <div style={{ textAlign: "center", color: "#575757" }}>
                    <UserOutlined style={{ fontSize: 70 }} />
                    <p className="user-info">
                      {user.alias}
                      <br />
                      <PhoneFilled />
                      &ensp;{user.countryCode}
                      {user.phone}
                      <br />
                      <MailFilled />
                      &ensp;{user.email}
                      <br />
                      <IdcardFilled />
                      &ensp;{startCase(merchant.topupPackage)}
                    </p>
                  </div>
                </Card>
              </Col>

              <Col span={6}>
                <Card style={{ width: 300 }}>
                  <div style={{ textAlign: "center", color: "#575757" }}>
                    <PayCircleOutlined style={{ fontSize: 70 }} />
                    <p className="user-info">
                      <br />
                      <BankFilled />
                      &ensp;{merchant?.bankName} ({merchant?.bankAccount})
                    </p>
                  </div>
                </Card>
              </Col>

              <Col span={6}>
                <Card style={{ width: 300 }}>
                  <div style={{ textAlign: "center", color: "#575757" }}>
                    <CreditCardFilled style={{ fontSize: 70 }} />
                    {/* cardBinding is missing (not null) when unbound; cardNumber is the legacy SenangPay card */}
                    <p className="user-info">
                      <br />
                      {merchant?.cardBinding ? (
                        <>
                          <Tag color="blue">{formatCardProvider(merchant.cardBinding.provider)}</Tag>
                          {formatCardSummary(merchant.cardBinding)}
                          <br />
                          <strong>Bound At:</strong>{" "}
                          {formatDateTime(merchant.cardBinding.boundAt)}
                        </>
                      ) : merchant?.cardNumber ? (
                        <>
                          <Tag>Legacy (SenangPay)</Tag>
                          {merchant.cardNumber}
                        </>
                      ) : (
                        <Tag>Not bound</Tag>
                      )}
                    </p>
                  </div>
                </Card>
              </Col>

              <Col span={6}>
                <Card style={{ width: 300 }}>
                  <div style={{ textAlign: "center", color: "#575757" }}>
                    <FileTextOutlined style={{ fontSize: 70 }} />
                    <p className="user-info">
                      <br />
                      <IdcardOutlined />
                      &ensp;<strong>BRN / IC:</strong> {merchant?.BRN}
                      <br />
                      <NumberOutlined />
                      &ensp;<strong>TIN:</strong> {merchant?.taxNo}
                      <br />
                      <FileTextOutlined />
                      &ensp;<strong>E-Invoice:</strong>{" "}
                      {merchant?.isEInvoice ? (
                        <CheckCircleTwoTone twoToneColor="#52c41a" />
                      ) : (
                        <CloseCircleTwoTone twoToneColor="#ff4d4f" />
                      )}
                      &ensp;{merchant?.isEInvoice ? "Opted In" : "Opted Out"}
                    </p>
                  </div>
                </Card>
              </Col>
              {
                user.isSubAccount && (
                  <Col span={6}>
                    <Card style={{ width: 300 }}>
                      <div style={{ textAlign: "center", color: "#575757" }}>
                        <GlobalOutlined style={{ fontSize: 70 }} />
                        <p className="user-info">
                          <br />
                          <IdcardOutlined />
                          &ensp;<strong>Master Email:</strong> {user?.master?.email}
                          <br />
                          <UserOutlined />
                          &ensp;<strong>Sub Account Role:</strong> {user?.perms}
                          <br />
                        </p>
                      </div>
                    </Card>
                  </Col>
                )
              }
            </Row>

            <div style={{ padding: "0 20px" }}>
              <Row gutter={[8, 8]}>
                <Col span={8}>Date Registration Account:</Col>
                <Col span={12}>
                  {user.createdAt ? formatDateTime(user.createdAt) : "-"}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Merchant Name :</Col>
                <Col span={12}>{merchant.name ? merchant.name : "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Merchant Id :</Col>
                <Col span={12}>{merchant.merchantId ? merchant.merchantId : "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Topup Package :</Col>
                <Col span={12}>
                  <TopupPackageTag packageKey={merchant.topupPackage} />
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Referral Code :</Col>
                <Col span={12}>
                  {user.referralCode ? user.referralCode : "-"}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Referral Code ID :</Col>
                <Col span={12}>
                  {user.referralCodeId ? user.referralCodeId : "-"}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Business Registration Number :</Col>
                <Col span={12}>{merchant.BRN ? merchant.BRN : "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Credits Balance:</Col>
                <Col span={12}>
                  {credits ? "RM" + merchant.credits.toFixed(2) : "-"}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Point Balance:</Col>
                <Col span={12}>
                  {user.pointBalance ?? 0}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Points Earned:</Col>
                <Col span={12}>
                  {user.totalPointsEarned ?? 0}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Points Redeemed:</Col>
                <Col span={12}>
                  {user.totalPointsRedeemed ?? 0}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Points Refunded:</Col>
                <Col span={12}>
                  {user.totalPointsRefunded ?? 0}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Total Topup :</Col>
                <Col span={12}>
                  {merchant.totalTopup ? "RM" + merchant.totalTopup : "-"}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Bank Name :</Col>
                <Col span={12}>
                  {merchant.bankName ? merchant.bankName : "-"}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Bank Account :</Col>
                <Col span={12}>
                  {merchant.bankAccount ? merchant.bankAccount : "-"}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Bank Holder Name :</Col>
                <Col span={12}>
                  {merchant.bankHolderName ? merchant.bankHolderName : "-"}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Legacy Card No. (SenangPay) :</Col>
                <Col span={12}>{merchant?.cardNumber || "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>
                  <Tooltip
                    placement="topLeft"
                    title="Active checkout orders only. Excludes Cancelled by User, Refunded, and Cancelled by Courier."
                  >
                    Total Order Parcel :
                  </Tooltip>
                </Col>
                <Col span={12}>
                  {user.totalNoOrder ? user.totalNoOrder : "0"}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Total Topup Transaction :</Col>
                <Col span={12}>{user?.totalTopupNo || "-"}</Col>
              </Row>
              {user?.ndrTrial?.startedAt && (
                <>
                  <Row gutter={[8, 8]}>
                    <Col span={8}>NDR Free Trial Start :</Col>
                    <Col span={12}>{user.ndrTrial.startedAt ? formatDateTime(user.ndrTrial.startedAt) : "-"}</Col>
                  </Row>
                  <Row gutter={[8, 8]}>
                    <Col span={8}>NDR Free Trial End :</Col>
                    <Col span={12}>{user.ndrTrial.expiresAt ? formatDateTime(user.ndrTrial.expiresAt) : "-"}</Col>
                  </Row>
                  <Row gutter={[8, 8]}>
                    <Col span={8}>NDR Free Trial Status :</Col>
                    <Col span={12}>{user.ndrTrial.state ? startCase(user.ndrTrial.state) : "-"}</Col>
                  </Row>
                </>
              )}
              <Row gutter={[8, 8]}>
                <Col span={8}>Where You Heard About Us : </Col>
                <Col span={12}>{user.heardFrom ? user.heardFrom : "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>API Source : </Col>
                <Col span={12}>{merchant.source ? merchant.source : "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Account Manager : </Col>
                <Col span={12}>{user.manager ? user.manager.email : "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Credit Tagging : </Col>
                <Col span={12}>{user.tagging ? user.tagging : "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Main Purpose : </Col>
                <Col span={12}>
                  {merchant.mainPurpose ? merchant.mainPurpose : "-"}
                </Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Business Name : </Col>
                <Col span={12}>{merchant.name ? merchant.name : "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>State : </Col>
                <Col span={12}>{user.baseState ? user.baseState : "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Website : </Col>
                <Col span={12}>{user.website ? user.website : "-"}</Col>
              </Row>
              <Row gutter={[8, 8]}>
                <Col span={8}>Monthly Parcel Volume : </Col>
                <Col span={12}>
                  {user.monthlyParcelVolume ? user.monthlyParcelVolume : "-"}
                </Col>
              </Row>
            </div>
          </TabPane>
          {permissions.includes(PERMISSIONS.VIEW_ANALYSIS_PAGE) && (
            <TabPane tab="ANALYTICS" key="analytics">
              {merchantId && <UserAnalytic merchantId={merchantId} />}
            </TabPane>
          )}

          {
            permissions.includes(PERMISSIONS.VIEW_TRANSACTION_PAGE) &&
            <TabPane tab="TRANSACTIONS" key="all-transactions">
              <Tabs defaultActiveKey="1" type="card" animated={true} size="small">
                <TabPane tab="ALL TRANSACTION" key="10" animated={false}>
                  {merchantId && (
                    <UserTransaction
                      merchantId={merchantId}
                      loading={loading}
                      method={""}
                    />
                  )}
                </TabPane>
                <TabPane tab="CONSIGNMENT PURCHASE" key="2" animated={false}>
                  {merchantId && (
                    <UserTransaction
                      merchantId={merchantId}
                      loading={loading}
                      method={"consignment_purchase"}
                    />
                  )}
                </TabPane>
                <TabPane tab="REFUND" key="3">
                  {merchantId && (
                    <UserTransaction
                      merchantId={merchantId}
                      loading={loading}
                      method={[
                        "Refund System Error",
                        "Refund Cancellation Order",
                      ]}
                    />
                  )}
                </TabPane>
                <TabPane tab="COD REFUND" key="4">
                  {merchantId && (
                    <UserTransaction
                      merchantId={merchantId}
                      loading={loading}
                      method={"refund-cod-charge"}
                    />
                  )}
                </TabPane>
                <TabPane tab="NEXT DAY REFUND" key="5">
                  {merchantId && (
                    <UserTransaction
                      merchantId={merchantId}
                      loading={loading}
                      method={"refund-next-day-remittance-charge"}
                    />
                  )}
                </TabPane>
                <TabPane tab="ONLINE PAYMENT" key="6">
                  {merchantId && (
                    <UserOnlinePayment
                      merchantId={merchantId}
                      loading={loading}
                    />
                  )}
                </TabPane>
                <TabPane tab="ADMIN ADJUSTMENT" key="7">
                  {merchantId && (
                    <UserAdminAdjustment
                      merchantId={merchantId}
                      loading={loading}
                    />
                  )}
                </TabPane>
                <TabPane tab="WEIGHT DISPUTE" key="8">
                  {merchantId && (
                    <UserTransaction
                      merchantId={merchantId}
                      loading={loading}
                      method={"weight-dispute"}
                    />
                  )}
                </TabPane>
                <TabPane tab="WEIGHT DISPUTE REFUND" key="weightDisputeRefund">
                  {merchantId && (
                    <UserTransaction
                      merchantId={merchantId}
                      loading={loading}
                      method={"refund-weight-dispute"}
                    />
                  )}
                </TabPane>
                <TabPane tab="RETURN HANDLING FEE REFUND" key="returnFeeRefund">
                  {merchantId && (
                    <UserTransaction
                      merchantId={merchantId}
                      loading={loading}
                      method={"Refund Return Handling Fee"}
                    />
                  )}
                </TabPane>
              </Tabs>
            </TabPane>
          }

          {permissions.includes(PERMISSIONS.VIEW_ORDER_PAGE) &&
            <TabPane tab="ALL ORDER" key="order-history">
              {merchantId && (
                <UserOrder merchantId={merchantId} userId={userId} />
              )}
            </TabPane>}
          {permissions.includes(PERMISSIONS.COD_ORDER_PAGE) &&
            <TabPane tab="COD ORDER" key="7">
              {merchantId && (
                <UserCodOrder merchantId={merchantId} userId={userId} />
              )}
            </TabPane>}
          {permissions.includes(PERMISSIONS.RETURNED_ORDER_PAGE) &&
            <TabPane tab="RETURNED ORDER" key="returned-order">
              {merchantId && (
                <UserReturnedOrder merchantId={merchantId} userId={userId} />
              )}
            </TabPane>
          }
          <TabPane tab="AUDIT LOG" key="8">
            <Pagination
              style={{ textAlign: "right" }}
              current={auditLogParams.currentPage}
              pageSize={auditLogParams.limit}
              total={auditLogData.totalSize}
              onChange={(page, pageSize) => {
                setAuditLogParams(prev => ({
                  ...prev,
                  currentPage: page,
                  limit: pageSize,
                }));
              }}
            />

            <AuditLogList auditLogs={auditLogData.data} />

            <Pagination
              style={{ textAlign: "right" }}
              current={auditLogParams.currentPage}
              pageSize={auditLogParams.limit}
              total={auditLogData.totalSize}
              onChange={(page, pageSize) => {
                setAuditLogParams(prev => ({
                  ...prev,
                  currentPage: page,
                  limit: pageSize,
                }));
              }}
            />
          </TabPane>
          <TabPane tab="FEATURES" key="beta-features">
            <UserBetaFeatures
              key={`${user.master?.id}-${user.inheritsFeatures}`}
              userId={userId}
              betaFeatures={betaFeatures}
              dhlCodLimit={dhlCodLimit}
              inheritedFeatures={user.inheritedFeatures}
              inheritsFeatures={user.inheritsFeatures}
              master={user.isSubAccount ? user.master : undefined}
              onFeaturesChange={setBetaFeatures}
            />
            {merchantId && (
              <UserPreferences merchantId={merchantId} currentPreferences={preferences} isSubAccount={user.isSubAccount} />
            )}
            {billingAllowed && (
              <UserBillingSettings userId={userId} billingType={billingType} creditTerm={creditTerm} />
            )}
          </TabPane>
          {(permissions?.includes(PERMISSIONS.VIEW_BILLING_STATEMENT_PAGE) ||
            permissions?.includes(PERMISSIONS.VIEW_BILLING_NOTES_PAGE)) &&
            billingAllowed && (
            <TabPane tab="BILLING" key="billing-statements">
              <UserBilling merchantId={merchantId} />
            </TabPane>
          )}
          {permissions.includes(PERMISSIONS.VIEW_ISSUES) && <TabPane
            tab={
              <Space align="center" justify="center">
                <Text>SUPPORT TICKETS</Text>
                {ticketsLoading.index && <LoadingOutlined color="blue" />}
              </Space>
            }
            key="support-tickets"
            disabled={ticketsLoading.index}
          >
            <Collapse
              defaultActiveKey={"filters"}
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
            >
              <Collapse.Panel header="Filters" key="filters">
                <SupportTicketFilters
                  filters={ticketFilters}
                  assignees={assignees}
                  submitters={submitters}
                  onFiltersChange={(filters) => setTicketFilters(filters)}
                  isUserDetails
                />
              </Collapse.Panel>
            </Collapse>

            <SupportTicketList
              supportTickets={supportTickets}
              loading={ticketsLoading.index}
              assignees={assignees}
            />
          </TabPane>}
          <TabPane tab="CREDIT CARD" key="credit-card">
            <UserCreditCard merchant={merchant} />
          </TabPane>
          <TabPane tab="POINT HISTORY" key="point-history">
            <UserPointHistory userId={userId} />
          </TabPane>
          {permissions.includes(PERMISSIONS.MANAGE_SUBACCOUNT) && (
            <TabPane tab="SUB ACCOUNT" key="sub-account">
              {getFeatureFlag("subAccountManagement") && (
                <UserSubAccountActions
                  userId={userId}
                  onChanged={() => {
                    loadUserDetails(userId);
                    loadAuditLog();
                  }}
                />
              )}
              {userId && (
                <SubAccountDetails
                  userId={userId}
                  masterFeatures={user.isSubAccount ? undefined : betaFeatures}
                />
              )}
            </TabPane>
          )}
        </Tabs>
      </div>
      {userNotes.length > 0 &&
        permissions.includes(PERMISSIONS.VIEW_USER_NOTES) && (
          <div>
            <Card className="w-full">
              <p className="detail-header-text">Notes : </p>
              {userNotes.map((note, index) => (
                <div>
                  <div key={index}>
                    <div style={{ fontSize: "14px", fontWeight: "600" }}>
                      {note.createdBy?.username}
                    </div>
                    <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                      {moment(note.createdAt).format(
                        "MMMM D, YYYY [at] h:mm A"
                      )}
                    </div>
                    <p style={{ marginTop: "8px", fontSize: "14px" }}>
                      {note.note}
                    </p>
                  </div>
                  <Divider></Divider>
                </div>
              ))}
              <Pagination
                style={{ textAlign: "right" }}
                total={userNotesPagination?.totalSize}
                onChange={(currentPage, limit) =>
                  setUserNotesPagination({
                    ...userNotesPagination,
                    limit,
                    currentPage,
                  })
                }
              />
            </Card>
          </div>
        )}
    </div>
  );
};

export default UserDetails;
